import { NotFoundError } from '@cian/peperrors/shared';

import {
  ERuntimeType,
  MANIFEST_API_RUNTIME_NAME,
  type IAPIRuntimeManifest,
  type IUIRuntimeManifest,
  type TRuntimeManifest,
  type IImageManifest,
} from '../../../shared/manifest/index.js';
import { generateTagName } from '../../../shared/runtime/tag.js';
import { BundleLoader, type Bundle } from '../bundles/index.js';
import { APIRuntime, UIRuntime } from '../runtime/index.js';

import type { IImageLoadable } from './interfaces.js';
import type { MicrofrontendNS } from '../../../shared/microfrontend/index.js';
import type { PartialBy } from '../../../shared/types/typescript/index.js';
import type { APIRuntimeBuilder, BundleExports, RuntimeBuilderFactory, UIRuntimeBuilder } from '../../bundles/index.js';
import type { IImage } from '../../image/index.js';
import type { IAPIRuntime, IUIRuntime } from '../../runtime/index.js';
import type { AssetsStorage } from '../assets/AssetsStorage.js';
import type { IRuntimeContext, IImageContext } from '../context/index.js';

export class Image implements IImageLoadable, IImage {
  private readonly manifest: IImageManifest;
  private readonly bundleLoader: BundleLoader;
  private readonly _runtimes: Map<MicrofrontendNS.RuntimeName, UIRuntime | APIRuntime> = new Map();
  private readonly context: IImageContext;

  public constructor(manifest: IImageManifest, assetsStorage: AssetsStorage, context: IImageContext) {
    this.manifest = manifest;
    this.context = context;

    this.bundleLoader = new BundleLoader(assetsStorage);

    for (const [bundleName, bundleManifest] of Object.entries(manifest.bundles)) {
      this.bundleLoader.createBundle(bundleName, bundleManifest, manifest.assets);
    }

    for (const rManifest of Object.values(manifest.runtimes)) {
      this.newRuntime(rManifest);
    }
  }

  public get microfrontend() {
    return this.context.microfrontend;
  }

  public get version() {
    return this.manifest.version;
  }

  public get assets() {
    return this.manifest.assets;
  }

  public get bundles() {
    return this.bundleLoader.bundles;
  }

  public get runtimes() {
    return Object.fromEntries<IAPIRuntime | IUIRuntime>(this._runtimes);
  }

  private get runtimeContext(): IRuntimeContext {
    return { microfrontend: this.context.microfrontend, image: this };
  }

  public runtime(name: MicrofrontendNS.RuntimeName) {
    return this._runtimes.get(name);
  }

  public runtimeStrict(name: MicrofrontendNS.RuntimeName) {
    const runtime = this.runtime(name);
    if (!runtime) {
      throw new NotFoundError({
        message: `Рантайм "${name as string}" не найден в реестре (${this.context.microfrontend.name}#${this.version})`,
        domain: 'mf-registry.browser.image.runtimeStrict',
      });
    }

    return runtime;
  }

  public newRuntime(manifest: IAPIRuntimeManifest): IAPIRuntime;
  public newRuntime(manifest: PartialBy<IUIRuntimeManifest, 'tagName'>): IUIRuntime;
  public newRuntime(manifest: TRuntimeManifest): IAPIRuntime | IUIRuntime;
  public newRuntime(
    manifest: IAPIRuntimeManifest | PartialBy<IUIRuntimeManifest, 'tagName'>,
  ): IAPIRuntime | IUIRuntime {
    let runtime: APIRuntime | UIRuntime;

    if (!this._runtimes.has(manifest.name)) {
      if (manifest.type === ERuntimeType.API) {
        runtime = new APIRuntime(manifest, this, this.runtimeContext);
      } else {
        runtime = new UIRuntime(
          {
            tagName: generateTagName({
              microfrontendName: this.context.microfrontend.name,
              imageVersion: this.version,
              runtimeName: manifest.name,
            }),
            ...manifest,
          },
          this,
          this.runtimeContext,
        );
      }

      this._runtimes.set(manifest.name, runtime);
    } else {
      runtime = this._runtimes.get(manifest.name) as APIRuntime | UIRuntime;
    }

    return runtime;
  }

  public async load(runtime: IAPIRuntimeManifest): Promise<RuntimeBuilderFactory<APIRuntimeBuilder>>;
  public async load(runtime: IUIRuntimeManifest): Promise<RuntimeBuilderFactory<UIRuntimeBuilder>>;
  public async load(
    runtime: IAPIRuntimeManifest | IUIRuntimeManifest,
  ): Promise<RuntimeBuilderFactory<APIRuntimeBuilder> | RuntimeBuilderFactory<UIRuntimeBuilder>> {
    /**
     * TODO: Приведение к типу необходимо, чтобы TS ниже не ругался на то, что у бандла unknown в экспортах.
     * По хорошему нужно либо строго требовать экспорт из бандлов заданной структуры, либо разрешить бандлы
     * без экспортов
     */
    const bundle = this.bundleLoader.bundle(runtime.bundle) as Bundle<BundleExports>;

    if (!bundle) {
      throw new NotFoundError({
        message: `Бандл для рантайма "${runtime.name}" не найден в реестре (${this.context.microfrontend.name}#${this.version})`,
        domain: 'mf-registry.browser.image.load',
      });
    }

    const mdl = await bundle.load();

    if (mdl.ui) {
      for (const runtimeName of Object.keys(mdl.ui)) {
        if (runtimeName === runtime.name) {
          continue;
        }

        this.newRuntime({
          type: ERuntimeType.UI,
          name: runtimeName,
          bundle: bundle.name,
          /**
           * FIXME: В разных версиях библиотеки @cian/mf-registry может отличаться алгоритм
           * генерации tagName. Если тэг будет генерироваться в CDN-части и на стороне микрофронта,
           * то высока вероятность, что это будут разные результаты. Таким образом, при регистрации
           * рантайма из бандла будет генерироваться один тэг, а при регистрации из манифеста микрофронта
           * будет генерироваться другой. Это может поломать обратную совместимость
           */
          tagName: generateTagName({
            microfrontendName: this.context.microfrontend.name,
            imageVersion: this.version,
            runtimeName,
          }),
        });
      }
    }

    if (mdl.api) {
      if (runtime.name !== MANIFEST_API_RUNTIME_NAME) {
        this.newRuntime({
          type: ERuntimeType.API,
          name: MANIFEST_API_RUNTIME_NAME,
          bundle: bundle.name,
        });
      }
    }

    if (!mdl[runtime.type]) {
      throw new NotFoundError({
        message: `В бандле для рантайма "${runtime.name}" не найдены экспортируемые ресурсы (${this.context.microfrontend.name}#${this.version})`,
        domain: 'mf-registry.browser.image.load',
      });
    }

    if (runtime.type === ERuntimeType.API) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return mdl.api!;
    }

    if (!mdl.ui || !mdl.ui[runtime.name]) {
      throw new NotFoundError({
        message: `В бандле для рантайма "${runtime.name}" не найдены экспортируемые ресурсы (${this.context.microfrontend.name}#${this.version})`,
        domain: 'mf-registry.browser.image.load',
      });
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return mdl.ui[runtime.name]!;
  }
}
